import { useState } from 'react';
import Icon from './Icon.jsx';
import './Pagination.css';

/**
 * 全站共用分頁器（商品一覽 / 虛擬商品 / 主題圖鑑等列表頁）。
 *
 * @param page        目前頁碼（1-based）
 * @param totalPages  總頁數
 * @param onChange    切頁 callback，傳入新頁碼
 *
 * 只顯示目前頁前後各 SIBLINGS 頁，不使用 … 收合；另附「共 N 頁」與跳頁輸入。
 * 用法：<Pagination page={page} totalPages={totalPages} onChange={setPage} />
 */

// 目前頁前後各顯示的頁碼數（視窗大小 = SIBLINGS * 2 + 1）
const SIBLINGS = 2;

// 產生要顯示的頁碼視窗；靠近頭尾時往內補滿，維持固定寬度
function buildWindow(page, total) {
  const size = Math.min(SIBLINGS * 2 + 1, total);
  let start = Math.max(1, page - SIBLINGS);
  const end = Math.min(total, start + size - 1);
  start = Math.max(1, end - size + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function Pagination({ page, totalPages, onChange }) {
  const [jump, setJump] = useState('');

  if (!totalPages || totalPages <= 1) return null;

  const go = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== page) onChange(next);
  };

  const submitJump = () => {
    const n = Number(jump);
    if (Number.isInteger(n) && n >= 1 && n <= totalPages) go(n);
    setJump('');
  };

  return (
    <nav className="pager" aria-label="分頁">
      <button
        type="button"
        className="pager-btn pager-edge"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="上一頁"
      >
        <Icon name="arrow-l" size={14} />
      </button>

      {buildWindow(page, totalPages).map((p) => (
        <button
          type="button"
          key={p}
          className={`pager-btn${p === page ? ' on' : ''}`}
          onClick={() => go(p)}
          aria-current={p === page ? 'page' : undefined}
        >
          {p}
        </button>
      ))}

      <button
        type="button"
        className="pager-btn pager-edge"
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        aria-label="下一頁"
      >
        <Icon name="arrow" size={14} />
      </button>

      <span className="pager-total">共 {totalPages} 頁</span>

      <span className="pager-jump">
        跳至
        <input
          className="pager-jump-input"
          type="number"
          min={1}
          max={totalPages}
          value={jump}
          onChange={(e) => setJump(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitJump(); }}
          aria-label="跳至頁碼"
        />
        頁
        <button
          type="button"
          className="pager-btn pager-jump-btn"
          onClick={submitJump}
        >
          前往
        </button>
      </span>
    </nav>
  );
}

export default Pagination;
